import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowUp,
  FaBuilding,
  FaCalendar,
  FaChartLine,
  FaClipboardCheck,
  FaClock,
  FaCogs,
  FaCoins,
  FaEnvelope,
  FaFileAlt,
  FaHandHoldingUsd,
  FaMoneyBillWave,
  FaPiggyBank,
  FaRobot,
  FaShoppingCart,
  FaUser,
  FaUserShield
} from 'react-icons/fa';
import { MdBusinessCenter, MdDashboard } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// Import Redux actions and selectors
import { useDrawer } from 'components/app/DrawerContext';
import { motion } from 'framer-motion';
import {
  getInvestorLandingData,
  getLandingData,
  selectError,
  selectInvestorLandingData,
  selectInvestorLandingDataStatus,
  selectLandingData,
  selectLandingDataStatus,
  selectLoading
} from '../../slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { avatarCacheManager } from '../../utils/avatarCache';
import AutomateContributionModal from './AutomateContributionModal';
import ShortcutNavigation from './ShortcutNavigation';

interface UnitClass {
  id: string;
  name: string;
  totalUnits: number;
  unitPrice: number;
  totalValue: number;
  description: string;
  performance: number;
  investorCount: number;
  additionalMetric: number;
  additionalMetricLabel: string;
  type: 'Income' | 'Growth';
  // Enhanced admin fields
  amountInvestedTillDate?: number;
  totalMonthlyIncome?: number;
  totalMonthlyExpense?: number;
  totalCashUsed?: number;
  totalPropertiesCurrentValue?: number;
  NAV?: number;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
  createdBy?: string;
  lastmodifiedBy?: string;
  // Investor specific fields
  investorName?: string;
  investorEmail?: string;
  investorAvatar?: string;
  operationCharge?: number;
  cashRemaining?: number;
}

const FundManagementInvestment: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fundManagementAdminRight, setFundManagementAdminRight } = useDrawer();

  // Redux state
  const landingData = useSelector(selectLandingData);
  const landingDataStatus = useSelector(selectLandingDataStatus);
  const investorLandingData = useSelector(selectInvestorLandingData);
  const investorLandingDataStatus = useSelector(selectInvestorLandingDataStatus);
  const error = useSelector(selectError);
  const loading = useSelector(selectLoading);

  // Use isAdmin state consistently throughout - declare early for dependencies
  const [isAdmin, setIsAdmin] = useState(fundManagementAdminRight);

  // Avatar state management with optimized loading
  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string | null }>({});
  const [avatarLoading, setAvatarLoading] = useState(false);
  const previousAvatarPaths = useRef<{
    incomeUnits?: string;
    growthUnits?: string;
  }>({});

  // Optimized avatar fetching using batch operations and caching
  useEffect(() => {
    const fetchAvatarsOptimized = async () => {
      if (!investorLandingData || isAdmin) return;

      const incomeAvatarPath = investorLandingData.incomeUnits?.investorAvatar;
      const growthAvatarPath = investorLandingData.growthUnits?.investorAvatar;

      // Skip if paths haven't changed
      if (
        previousAvatarPaths.current.incomeUnits === incomeAvatarPath &&
        previousAvatarPaths.current.growthUnits === growthAvatarPath
      ) {
        return;
      }

      setAvatarLoading(true);
      try {
        // Batch fetch avatars with concurrency control
        const avatarPaths = [incomeAvatarPath, growthAvatarPath];
        const results = await avatarCacheManager.fetchAvatarsBatch(avatarPaths, 2);

        setAvatarUrls({
          incomeUnits: results.get(incomeAvatarPath!) || null,
          growthUnits: results.get(growthAvatarPath!) || null
        });

        previousAvatarPaths.current = {
          incomeUnits: incomeAvatarPath,
          growthUnits: growthAvatarPath
        };
      } catch (error) {
        console.error('Error fetching avatars:', error);
      } finally {
        setAvatarLoading(false);
      }
    };

    if (investorLandingData && !isAdmin) {
      fetchAvatarsOptimized();
    }

    // Cleanup on unmount
    return () => {
      const urlsToRevoke = Object.values(avatarUrls).filter((url) => !!url);
      if (urlsToRevoke.length > 0) {
        avatarCacheManager.revokeBlobUrls(urlsToRevoke);
      }
    };
  }, [investorLandingData, isAdmin]);

  // Check if user has admin privileges
  const hasAdminPrivileges = (() => {
    const loginInfo = localStorage.getItem('user-details');
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
      projectName?: string;
      isAdmin?: boolean;
    };

    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return (
          selectedProject?.isAdmin === true &&
          userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin')
        );
      } catch (error) {
        return false;
      }
    }
    return false;
  })();

  const lastLoadedRequestKeyRef = useRef<string>('');
  const initialLoadCompletedRef = useRef<{ admin: boolean; investor: boolean }>({
    admin: false,
    investor: false
  });

  useEffect(() => {
    if (landingDataStatus === 'succeeded' || landingDataStatus === 'failed') {
      initialLoadCompletedRef.current.admin = true;
    }
    if (investorLandingDataStatus === 'succeeded' || investorLandingDataStatus === 'failed') {
      initialLoadCompletedRef.current.investor = true;
    }
  }, [landingDataStatus, investorLandingDataStatus]);

  useEffect(() => {
    setFundManagementAdminRight(isAdmin);
  }, [isAdmin, setFundManagementAdminRight]);

  // Check if we're on the main dashboard route
  const isMainDashboard =
    location.pathname === '/fundManagementInvestment' ||
    location.pathname === '/fundManagementInvestment/';

  // Trust Fund Data
  const [trustData] = useState({
    trustName: 'Growth & Income Unit Trust',
    inceptionDate: '2020-01-15'
  });
  const [includeInvestorGraph, setIncludeInvestorGraph] = useState(false);

  // Lazy-load investor graph data only when investor dashboard is active.
  useEffect(() => {
    if (isAdmin || !isMainDashboard) {
      setIncludeInvestorGraph(false);
      return;
    }

    const timerId = setTimeout(() => {
      setIncludeInvestorGraph(true);
    }, 400);

    return () => clearTimeout(timerId);
  }, [isAdmin, isMainDashboard]);

  // Helper functions with memoization to avoid recreating on every render
  const getCurrentUserEmail = useCallback(() => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return userDetails?.email || '';
      } catch (error) {
        return '';
      }
    }
    return '';
  }, []);

  const getGroupId = useCallback(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
      projectName?: string;
      isAdmin?: boolean;
    };
    return selectedProject?.projectId || '';
  }, []);

  // Consolidated data loading effect (replaces two separate useEffects)
  useEffect(() => {
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;
    const maxRetries = 6;

    const loadData = () => {
      const groupId = getGroupId();
      if (!groupId) {
        if (retryCount < maxRetries) {
          retryCount += 1;
          retryTimeoutId = setTimeout(loadData, 500);
        }
        return;
      }

      console.log('Loading fund management data...', { isAdmin, hasAdminPrivileges, groupId });

      if (isAdmin && hasAdminPrivileges) {
        const requestKey = `admin:${groupId}`;
        const shouldSkipAdminReload =
          lastLoadedRequestKeyRef.current === requestKey && landingDataStatus !== 'failed';
        if (shouldSkipAdminReload) return;
        lastLoadedRequestKeyRef.current = requestKey;

        // Load admin data
        dispatch(
          getLandingData({
            groupId: groupId,
            serviceId: 'fundManagementInvestment',
            isAdmin: true
          })
        );
      } else {
        // Load investor data whenever admin mode is not both enabled and authorized.
        const userEmail = getCurrentUserEmail();
        if (!userEmail) {
          if (retryCount < maxRetries) {
            retryCount += 1;
            retryTimeoutId = setTimeout(loadData, 500);
          }
          return;
        }

        const requestKey = `investor:${groupId}:${userEmail}:graph:${includeInvestorGraph ? '1' : '0'}`;
        const shouldSkipInvestorReload =
          lastLoadedRequestKeyRef.current === requestKey &&
          investorLandingDataStatus !== 'failed' &&
          landingDataStatus !== 'failed';
        if (shouldSkipInvestorReload) return;
        lastLoadedRequestKeyRef.current = requestKey;

        dispatch(
          getInvestorLandingData({
            groupId: groupId,
            serviceId: 'fundManagementInvestment',
            investorsEmailId: userEmail,
            includeGraph: includeInvestorGraph,
            includeRecords: false,
            range: '3M'
          })
        );

        // Also fetch admin data for unit prices (only once via caching)
        dispatch(
          getLandingData({
            groupId: groupId,
            serviceId: 'fundManagementInvestment',
            isAdmin: true
          })
        );
      }
    };

    loadData();

    const handleWindowFocus = () => {
      retryCount = 0;
      loadData();
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [
    isAdmin,
    getGroupId,
    getCurrentUserEmail,
    dispatch,
    hasAdminPrivileges,
    includeInvestorGraph,
    landingDataStatus,
    investorLandingDataStatus
  ]);

  // Automation modal state
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [selectedUnitType, setSelectedUnitType] = useState<'Income' | 'Growth'>('Income');

  // Format date for display
  const formatDateTime = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return date.toLocaleDateString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Enhanced memoized unit classes with unit price from admin data for investors
  const unitClasses = useMemo((): UnitClass[] => {
    // Initial unit classes - these will always be shown for symmetry
    const initialUnitClasses: UnitClass[] = [
      {
        id: 'initial-income',
        name: 'Income Units',
        totalUnits: 0,
        unitPrice: 1.0,
        totalValue: 0,
        description:
          'Conservative investment focused on regular income generation through quarterly distributions',
        performance: 0,
        investorCount: 0,
        additionalMetric: 0,
        additionalMetricLabel: 'Monthly Income',
        type: 'Income',
        // Add N/A values for admin fields
        amountInvestedTillDate: 0,
        totalMonthlyIncome: 0,
        totalMonthlyExpense: 0,
        totalCashUsed: 0,
        totalPropertiesCurrentValue: 0,
        NAV: 0,
        createdDateTime: undefined,
        lastUpdatedDateTime: undefined,
        createdBy: undefined,
        lastmodifiedBy: undefined
      },
      {
        id: 'initial-growth',
        name: 'Growth Units',
        totalUnits: 0,
        unitPrice: 1.0,
        totalValue: 0,
        description:
          'Growth-focused investment for long-term capital appreciation with higher risk tolerance',
        performance: 0,
        investorCount: 0,
        additionalMetric: 0,
        additionalMetricLabel: 'Cash Used',
        type: 'Growth',
        // Add N/A values for admin fields
        amountInvestedTillDate: 0,
        totalMonthlyIncome: 0,
        totalMonthlyExpense: 0,
        totalCashUsed: 0,
        totalPropertiesCurrentValue: 0,
        NAV: 0,
        createdDateTime: undefined,
        lastUpdatedDateTime: undefined,
        createdBy: undefined,
        lastmodifiedBy: undefined
      }
    ];

    let processedData = [...initialUnitClasses];

    // Get admin unit prices (for both admin and investor views)
    const adminUnitPrices: { [key: string]: number } = {};
    const adminUnitExists: { [key: string]: boolean } = { Income: false, Growth: false };

    if (landingData && Array.isArray(landingData) && landingData.length > 0) {
      landingData.forEach((unit) => {
        // Check if unit object is not empty
        const isValidUnit = unit && Object.keys(unit).length > 0;

        if (isValidUnit && unit.unitType && unit.unitPrice) {
          const unitType = unit.unitType === 'incomeUnits' ? 'Income' : 'Growth';
          adminUnitPrices[unitType] = parseFloat(unit.unitPrice) || 1.0;
          adminUnitExists[unitType] = true;
        }
      });
    }

    if (isAdmin && hasAdminPrivileges) {
      // ADMIN VIEW: Process admin landing data with enhanced fields
      if (
        landingDataStatus === 'succeeded' &&
        Array.isArray(landingData) &&
        landingData.length > 0
      ) {
        try {
          landingData.forEach((unit) => {
            // Check if unit is not an empty object
            const isNotEmpty = unit && Object.keys(unit).length > 0;

            const hasValidData =
              isNotEmpty &&
              unit.hasOwnProperty('unitType') &&
              unit.hasOwnProperty('unitPrice') &&
              unit.unitPrice !== undefined &&
              unit.unitPrice !== null &&
              unit.unitPrice !== '' &&
              (unit.unitType === 'growthUnits' || unit.unitType === 'incomeUnits');

            if (hasValidData) {
              const totalUnits = parseFloat(unit.totalUnitsHoldTillDate) || 0;
              const unitPrice = parseFloat(unit.unitPrice) || 1.0;
              const totalNAV = parseFloat(unit.NAV) || 0;
              const amountInvested = parseFloat(unit.amountInvestedTillDate) || 0;
              const totalInvestors = unit.totalInvestorsCount || 0;
              const totalCashUsed = parseFloat(unit.totalCashUsed) || 0;
              const totalMonthlyIncome = parseFloat(unit.totalMonthlyIncome) || 0;
              const totalMonthlyExpense = parseFloat(unit.totalMonthlyExpense) || 0;
              const totalPropertiesCurrentValue =
                parseFloat(unit?.totalPropertiesCurrentValue || '0') || 0;

              const performance =
                amountInvested > 0 ? ((totalNAV - amountInvested) / amountInvested) * 100 : 0;
              const isIncomeUnit = unit.unitType === 'incomeUnits';
              const unitType = isIncomeUnit ? 'Income' : 'Growth';
              const unitName = isIncomeUnit ? 'Income Units' : 'Growth Units';

              const additionalMetric = isIncomeUnit ? totalMonthlyIncome : totalCashUsed;
              const additionalMetricLabel = isIncomeUnit ? 'Monthly Income' : 'Cash Used';

              const processedUnit: UnitClass = {
                id: `admin-${unit.unitType}`,
                name: unitName,
                totalUnits: totalUnits,
                unitPrice: unitPrice,
                totalValue: totalNAV,
                description: isIncomeUnit
                  ? 'Conservative investment focused on regular income generation through quarterly distributions'
                  : 'Growth-focused investment for long-term capital appreciation with higher risk tolerance',
                performance: Math.round(performance * 10) / 10,
                investorCount: totalInvestors,
                additionalMetric: additionalMetric,
                additionalMetricLabel: additionalMetricLabel,
                type: unitType as 'Income' | 'Growth',
                // Enhanced admin fields
                amountInvestedTillDate: amountInvested,
                totalMonthlyIncome: totalMonthlyIncome,
                totalMonthlyExpense: totalMonthlyExpense,
                totalCashUsed: totalCashUsed,
                totalPropertiesCurrentValue: totalPropertiesCurrentValue,
                NAV: totalNAV,
                createdDateTime: unit.createdDateTime,
                lastUpdatedDateTime: unit.lastUpdatedDateTime,
                createdBy: unit.createdBy,
                lastmodifiedBy: unit.lastmodifiedBy,
                //
                cashRemaining: parseFloat(unit.cashRemaining || '0'),
                operationCharge: parseFloat(unit.operationCharge || '0')
              };

              const targetIndex = processedData.findIndex((item) => item.type === unitType);
              if (targetIndex !== -1) {
                processedData[targetIndex] = processedUnit;
              }
            }
          });

          // Apply admin unit prices to cards that remain initial
          processedData = processedData.map((item) => {
            if (
              (item.id === 'initial-income' || item.id === 'initial-growth') &&
              adminUnitPrices[item.type]
            ) {
              return {
                ...item,
                unitPrice: adminUnitPrices[item.type]
              };
            }
            return item;
          });
        } catch (err) {
          console.error('Error processing admin API data:', err);
        }
      }
    } else {
      // INVESTOR VIEW: Process investor landing data but use admin unit prices
      if (investorLandingDataStatus === 'succeeded' && investorLandingData) {
        try {
          // Process income units
          if (investorLandingData.incomeUnits) {
            const incomeData = investorLandingData.incomeUnits;
            const amountInvested = parseFloat(incomeData.amountInvestedTillDate) || 0;
            const totalUnits = parseFloat(incomeData.totalUnitsHoldTillDate) || 0;
            const hasValidIncomeData =
              incomeData.investorName &&
              incomeData.investorName.trim() !== '' &&
              (totalUnits > 0 || amountInvested > 0);

            if (hasValidIncomeData) {
              const monthlyIncome = parseFloat(incomeData.monthlyIncome || '0') || 0;

              // Use admin unit price if available, otherwise calculate from investment data
              const unitPrice =
                adminUnitPrices['Income'] || (totalUnits > 0 ? amountInvested / totalUnits : 1.0);

              // Calculate current value
              const currentValue = totalUnits * unitPrice;

              const incomeUnit: UnitClass = {
                id: 'investor-income',
                name: 'My Income Units',
                totalUnits: totalUnits,
                unitPrice: unitPrice,
                totalValue: currentValue, // Current value (units × price)
                description: 'Your conservative investment generating regular income distributions',
                performance: 0,
                investorCount: 1,
                additionalMetric: monthlyIncome,
                additionalMetricLabel: 'Monthly Income',
                type: 'Income',
                // Enhanced investor details
                investorName: incomeData.investorName,
                investorEmail: incomeData.investorsEmailId,
                investorAvatar: incomeData.investorAvatar,
                createdDateTime: incomeData.createdDateTime,
                lastUpdatedDateTime: incomeData.lastUpdatedDateTime
              };

              const targetIndex = processedData.findIndex((item) => item.type === 'Income');
              if (targetIndex !== -1) {
                processedData[targetIndex] = incomeUnit;
              }
            }
          }

          // Process growth units
          if (investorLandingData.growthUnits) {
            const growthData = investorLandingData.growthUnits;
            const amountInvested = parseFloat(growthData.amountInvestedTillDate) || 0;
            const totalUnits = parseFloat(growthData.totalUnitsHoldTillDate) || 0;
            const hasValidGrowthData =
              growthData.investorName &&
              growthData.investorName.trim() !== '' &&
              (totalUnits > 0 || amountInvested > 0);

            if (hasValidGrowthData) {

              // Use admin unit price if available, otherwise calculate from investment data
              const unitPrice =
                adminUnitPrices['Growth'] || (totalUnits > 0 ? amountInvested / totalUnits : 1.0);

              // Calculate current investment value for Growth (units × current price)
              const currentInvestmentValue = totalUnits * unitPrice;

              const growthUnit: UnitClass = {
                id: 'investor-growth',
                name: 'My Growth Units',
                totalUnits: totalUnits,
                unitPrice: unitPrice,
                totalValue: currentInvestmentValue, // SWAPPED: Now shows current value (units × price)
                description: 'Your growth-focused investment for long-term capital appreciation',
                performance: 0,
                investorCount: 1,
                additionalMetric: amountInvested, // SWAPPED: Now shows original invested amount
                additionalMetricLabel: 'My Investment', // Changed label
                type: 'Growth',
                // Enhanced investor details
                investorName: growthData.investorName,
                investorEmail: growthData.investorsEmailId,
                investorAvatar: growthData.investorAvatar,
                createdDateTime: growthData.createdDateTime,
                lastUpdatedDateTime: growthData.lastUpdatedDateTime
              };

              const targetIndex = processedData.findIndex((item) => item.type === 'Growth');
              if (targetIndex !== -1) {
                processedData[targetIndex] = growthUnit;
              }
            }
          }

          // Apply admin unit prices to cards that remain initial
          processedData = processedData.map((item) => {
            if (
              (item.id === 'initial-income' || item.id === 'initial-growth') &&
              adminUnitPrices[item.type]
            ) {
              return {
                ...item,
                unitPrice: adminUnitPrices[item.type]
              };
            }
            return item;
          });
        } catch (err) {
          console.error('Error processing investor API data:', err);
        }
      }
    }

    return processedData;
  }, [
    landingData,
    landingDataStatus,
    investorLandingData,
    investorLandingDataStatus,
    isAdmin,
    hasAdminPrivileges
  ]);

  // Navigation shortcuts
  const adminNavigationShortcuts = [
    {
      title: 'Dashboard',
      description: 'Unit trust performance, NAV tracking, and investor analytics',
      icon: <MdDashboard size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/fundManagementInvestment/dashboard',
      isActive: true
    },
    {
      title: 'Purchase Units',
      description: 'Process new unit subscriptions and manage investor onboarding',
      icon: <FaCoins size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      path: '/fundManagementInvestment/purchase-units',
      isActive: true
    },
    {
      title: 'Sell Units',
      description: 'Process unit redemptions and manage investor withdrawals',
      icon: <FaMoneyBillWave size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      path: '/fundManagementInvestment/sell-units',
      isActive: true
    },
    {
      title: 'Purchase Assets',
      description: 'Acquire new properties and assets for the trust portfolio',
      icon: <MdBusinessCenter size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      path: '/fundManagementInvestment/purchase-assets',
      isActive: true
    },
    {
      title: 'Sell Assets',
      description: 'Divest assets from the trust portfolio and process asset sales',
      icon: <FaHandHoldingUsd size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      path: '/fundManagementInvestment/sell-assets',
      isActive: true
    },
    {
      title: 'Approval Requests',
      description: 'Review and approve unit purchases, redemptions, and asset transactions',
      icon: <FaClipboardCheck size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      path: '/fundManagementInvestment/request',
      isActive: true
    },
    {
      title: 'Assets List',
      description: 'View and manage all assets within the trust portfolio',
      icon: <FaBuilding size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      path: '/fundManagementInvestment/all-assets',
      isActive: true
    },
    {
      title: 'Reports',
      description: 'Generate unit purchase statements, BAS reports, and audit documents',
      icon: <FaFileAlt size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      path: '/fundManagementInvestment/reports',
      isActive: true
    },
    {
      title: 'Group Report',
      description: 'View group-level member and transaction reporting data',
      icon: <FaFileAlt size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #cfd9df 0%, #e2ebf0 100%)',
      path: '/fundManagementInvestment/group-report',
      isActive: true
    },
    {
      title: 'Simulation',
      description: 'Financial modeling, BAS calculations, and investment scenario analysis',
      icon: <FaChartLine size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
      path: '/fundManagementInvestment/simulation',
      isActive: true
    },
    {
      title: 'Configuration',
      description: 'System setup, user roles, permissions, and core settings management',
      icon: <FaCogs size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
      path: '/fundManagementInvestment/configuration',
      isActive: true
    }
  ];

  const investorNavigationShortcuts = [
    {
      title: 'Dashboard',
      description: 'Unit trust performance, NAV tracking, and investor analytics',
      icon: <MdDashboard size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      path: '/fundManagementInvestment/dashboard',
      isActive: true
    },
    {
      title: 'Purchase Units',
      description: 'Process new unit subscriptions and manage investor onboarding',
      icon: <FaShoppingCart size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      path: '/fundManagementInvestment/purchase-units',
      isActive: true
    },
    {
      title: 'Sell Units',
      description: 'Process unit redemptions and manage investor withdrawals',
      icon: <FaMoneyBillWave size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      path: '/fundManagementInvestment/sell-units',
      isActive: true
    },
    {
      title: 'Request Status',
      description: 'Track the status of your pending subscription and redemption requests',
      icon: <FaClock size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      path: '/fundManagementInvestment/request',
      isActive: true
    },
    {
      title: 'Assets List',
      description: 'View and manage all assets within the trust portfolio',
      icon: <FaBuilding size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      path: '/fundManagementInvestment/all-assets',
      isActive: true
    },
    {
      title: 'Reports',
      description: 'Download your statements, tax documents, and transaction reports',
      icon: <FaFileAlt size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      path: '/fundManagementInvestment/reports',
      isActive: true
    },
    {
      title: 'Group Report',
      description: 'View group-level member and transaction reporting data',
      icon: <FaFileAlt size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #cfd9df 0%, #e2ebf0 100%)',
      path: '/fundManagementInvestment/group-report',
      isActive: true
    },
    {
      title: 'Configuration',
      description: 'System setup, user roles, permissions, and core settings management',
      icon: <FaCogs size={isMobile ? 16 : 18} />,
      gradient: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
      path: '/fundManagementInvestment/configuration',
      isActive: true
    }
  ];

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;

    let formattedAmount;

    if (absAmount >= 1000000000) {
      // Billions
      formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
    } else if (absAmount >= 1000000) {
      // Millions
      formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
    } else if (absAmount >= 1000) {
      // Thousands
      formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
    } else {
      // Less than 1000, show as is
      formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(2);
    }

    // Remove unnecessary trailing zeros after decimal point
    if (formattedAmount.includes('.')) {
      formattedAmount = formattedAmount.replace(/\.?0+$/, '');
    }

    const sign = isNegative ? '-' : '';
    return `${sign}$${formattedAmount}`;
  };

  // Helper function to determine data source status
  const getDataSourceStatus = () => {
    const currentStatus = isAdmin ? landingDataStatus : investorLandingDataStatus;
    const currentLoading = loading || currentStatus === 'pending';

    if (currentLoading || currentStatus === 'idle') {
      return { text: 'Loading fund data...', color: '#ff9800' };
    }

    if (currentStatus === 'failed') {
      return { text: 'Initial setup (Connection issue)', color: '#f44336' };
    }

    if (currentStatus === 'succeeded') {
      const hasLiveData = unitClasses.some(
        (unit) => unit.id.includes('admin') || unit.id.includes('investor')
      );
      if (hasLiveData) {
        return { text: isAdmin ? 'Live fund data' : 'Your portfolio data', color: '#4caf50' };
      } else {
        return { text: 'Initial setup (First investor)', color: '#2196f3' };
      }
    }

    return { text: 'Initial setup', color: '#2196f3' };
  };

  const handleAutomationClick = (unitType: 'Income' | 'Growth') => {
    setSelectedUnitType(unitType);
    setAutomationModalOpen(true);
  };

  // If we're on a nested route, show only the nested component
  if (!isMainDashboard) {
    return <Outlet />;
  }

  // Show loading state for API data
  const currentStatus = isAdmin ? landingDataStatus : investorLandingDataStatus;
  const hasInitialLoadForCurrentMode = isAdmin
    ? initialLoadCompletedRef.current.admin
    : initialLoadCompletedRef.current.investor;
  const isCurrentlyLoading =
    !hasInitialLoadForCurrentMode &&
    (loading || currentStatus === 'idle' || currentStatus === 'pending');

  if (isCurrentlyLoading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          padding: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Loading {isAdmin ? 'fund' : 'portfolio'} data...
          </Typography>
        </Box>
      </Container>
    );
  }

  const dataStatus = getDataSourceStatus();

  return (
    <Container
      maxWidth={false}
      sx={{
        padding: { xs: 1, sm: 1.5, md: 2, lg: 3 },
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)'
      }}
    >
      {/* Enhanced Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          marginBottom: { xs: 2, md: 2.5 },
          gap: { xs: 1.5, sm: 2, md: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: { xs: 1, md: 1.5 },
              flexShrink: 0
            }}
          >
            <FaCoins size={isMobile ? 14 : 16} color="white" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 500,
                color: '#333',
                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                lineHeight: 1.2
              }}
            >
              {trustData.trustName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                lineHeight: 1.3
              }}
            >
              {isAdmin ? 'Fund Management Dashboard' : 'My Investment Portfolio'} •{' '}
              {trustData.inceptionDate}
              <span style={{ marginLeft: '8px', color: dataStatus.color }}>
                • {dataStatus.text}
              </span>
            </Typography>
          </Box>
        </Box>

        {/* Role Toggle - Only show if user has admin privileges */}
        {hasAdminPrivileges && (
          <Box
            sx={{
              padding: { xs: 0.5, sm: 1 }
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                borderRadius: 1.5,
                overflow: 'hidden',
                cursor: 'pointer',
                userSelect: 'none',
                width: 130,
                border: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              {/* Sliding Highlight */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: isAdmin ? '50%' : '0%',
                  width: '50%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: 1.5,
                  transition: 'left 0.4s ease',
                  zIndex: 0
                }}
              />

              {/* Investor Button */}
              <Box
                onClick={() => setIsAdmin(false)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  padding: 0.5,
                  color: !isAdmin ? '#fff' : '#555',
                  fontWeight: 600,
                  position: 'relative',
                  zIndex: 1,
                  transition: 'color 0.4s ease'
                }}
              >
                <FaUser size={12} />
                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                  Investor
                </Typography>
              </Box>

              {/* Admin Button */}
              <Box
                onClick={() => setIsAdmin(true)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  padding: 0.5,
                  color: isAdmin ? '#fff' : '#555',
                  fontWeight: 600,
                  position: 'relative',
                  zIndex: 1,
                  transition: 'color 0.4s ease'
                }}
              >
                <FaUserShield size={12} />
                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                  Admin
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Investor Personal Details Card - Only show for investor view with live data */}
      {!isAdmin && unitClasses.some((unit) => unit.investorName) && (
        <Box sx={{ marginBottom: { xs: 1.5, md: 2 } }}>
          {unitClasses
            .filter((unit) => unit.investorName && unit.id.includes('investor'))
            .slice(0, 1)
            .map((unit) => (
              <Card
                key="investor-details"
                sx={{
                  borderRadius: { xs: 1.5, md: 2 },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#f5f5f5',
                  marginBottom: { xs: 1, md: 1.5 },
                  boxShadow: {
                    xs: '0 2px 12px rgba(0, 0, 0, 0.12)',
                    md: '0 4px 20px rgba(0, 0, 0, 0.12)'
                  },
                  overflow: 'hidden'
                }}
              >
                <CardContent
                  sx={{
                    padding: { xs: 1.5, sm: 2, md: 2.5 },
                    '&:last-child': { paddingBottom: { xs: 1.5, sm: 2, md: 2.5 } }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1.5, sm: 2 },
                      width: '100%'
                    }}
                  >
                    {/* Avatar and User Info Section */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                        flex: { xs: 'none', sm: 1 },
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: 0
                      }}
                    >
                      <Avatar
                        src={
                          avatarUrls[unit.type === 'Income' ? 'incomeUnits' : 'growthUnits'] ||
                          undefined
                        }
                        sx={{
                          width: { xs: 40, sm: 50, md: 56 },
                          height: { xs: 40, sm: 50, md: 56 },
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                          fontWeight: 600,
                          color: '#fff',
                          flexShrink: 0
                        }}
                      >
                        {unit.investorName?.charAt(0) || 'I'}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: '#ffffff',
                            mb: 0.5,
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            lineHeight: 1.2,
                            wordBreak: 'break-word'
                          }}
                        >
                          {unit.investorName}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'column' },
                            gap: { xs: 0.3, sm: 0.5 }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <FaEnvelope size={10} color="#dcdcdc" />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#e0e0e0',
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                wordBreak: 'break-all'
                              }}
                            >
                              {unit.investorEmail}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <FaCalendar size={10} color="#dcdcdc" />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#e0e0e0',
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                              }}
                            >
                              Member since {formatDateTime(unit.createdDateTime)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Portfolio Value Section - Calculate based on current unit price */}
                    <Box
                      sx={{
                        textAlign: { xs: 'left', sm: 'right' },
                        alignSelf: { xs: 'stretch', sm: 'center' },
                        minWidth: { xs: 'auto', sm: '100px', md: '120px' },
                        mt: { xs: 0.8, sm: 0 },
                        pt: { xs: 1, sm: 0 },
                        borderTop: { xs: '1px solid rgba(255, 255, 255, 0.2)', sm: 'none' },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: '#ffffff',
                          mb: 0.3,
                          fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.2rem' },
                          lineHeight: 1.1,
                          wordBreak: 'break-word'
                        }}
                      >
                        {formatCurrency(
                          unitClasses.reduce((total, u) => {
                            // Calculate current value for each unit: units × current price
                            const currentValue = u.totalUnits * u.unitPrice;
                            return total + currentValue;
                          }, 0)
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#e0e0e0',
                          fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                          lineHeight: 1.3
                        }}
                      >
                        Total Portfolio Value
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Box>
      )}

      {/* Unit Cards Section */}
      <Box sx={{ marginBottom: { xs: 2.5, md: 3 } }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            marginBottom: { xs: 1.5, md: 1.5 },
            color: '#333',
            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
          }}
        >
          {isAdmin ? 'Unit Type Performance Overview' : 'My Unit Holdings Summary'}
        </Typography>

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          {unitClasses.map((unitClass) => (
            <Grid item xs={12} lg={6} key={unitClass.id}>
              <Card
                sx={{
                  borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  boxShadow: {
                    xs: '0 2px 8px rgba(0, 0, 0, 0.06)',
                    md: '0 3px 16px rgba(0, 0, 0, 0.08)'
                  },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-1px)' },
                    boxShadow: {
                      xs: '0 2px 8px rgba(0, 0, 0, 0.06)',
                      md: '0 6px 24px rgba(0, 0, 0, 0.12)'
                    }
                  }
                }}
              >
                {/* Header with Gradient */}
                <Box
                  sx={{
                    background:
                      unitClass.type === 'Income'
                        ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                        : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    padding: { xs: 1.2, sm: 1.5, md: 2 },
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: { xs: 1, md: 1.5 },
                      position: 'relative',
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      gap: { xs: 0.8, sm: 0 }
                    }}
                  >
                    <Avatar
                      src={undefined}
                      sx={{
                        width: { xs: 28, sm: 32, md: 38 },
                        height: { xs: 28, sm: 32, md: 38 },
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        marginRight: { xs: 0.8, md: 1 },
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {unitClass.type === 'Income' ? (
                        <FaPiggyBank size={isMobile ? 12 : 16} />
                      ) : (
                        <FaArrowUp size={isMobile ? 12 : 16} />
                      )}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 0.5, sm: 0.8 },
                          marginBottom: 0.3,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {unitClass.name}
                        </Typography>
                        <Chip
                          label={
                            unitClass.id.includes('admin') || unitClass.id.includes('investor')
                              ? 'Live'
                              : 'Initial'
                          }
                          size="small"
                          sx={{
                            backgroundColor:
                              unitClass.id.includes('admin') || unitClass.id.includes('investor')
                                ? 'rgba(76, 175, 80, 0.3)'
                                : 'rgba(158, 158, 158, 0.3)',
                            color: 'white',
                            fontWeight: 500,
                            fontSize: { xs: '0.5rem', sm: '0.55rem' },
                            height: { xs: 16, sm: 18 },
                            '& .MuiChip-label': {
                              px: { xs: 0.5, sm: 1 }
                            }
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.9,
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                          lineHeight: 1.3
                        }}
                      >
                        {unitClass.totalUnits.toLocaleString()} units • $
                        {unitClass.unitPrice.toFixed(4)} per unit
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' },
                        mt: { xs: 1, sm: 0 }
                      }}
                    >
                      {isAdmin &&
                        (unitClass.id.includes('admin') || unitClass.id.includes('initial')) && (
                          <motion.div
                            whileHover={{
                              scale: 1.05,
                              y: -1,
                              transition: { duration: 0.2 }
                            }}
                            whileTap={{
                              scale: 0.95,
                              transition: { duration: 0.1 }
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={
                                <motion.div
                                  animate={{
                                    rotate: [0, -10, 10, -10, 0],
                                    y: [0, -2, 0, -1, 0],
                                    scale: [1, 1.1, 1, 1.05, 1]
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    repeatType: 'loop',
                                    ease: 'easeInOut'
                                  }}
                                >
                                  <FaRobot size={isMobile ? 10 : 11} />
                                </motion.div>
                              }
                              onClick={() => handleAutomationClick(unitClass.type)}
                              sx={{
                                backgroundColor: '#ffffff',
                                color: '#4a4a4a',
                                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                                fontWeight: 600,
                                height: { xs: 24, sm: 26 },
                                minWidth: 'auto',
                                px: { xs: 1, sm: 1.5 },
                                borderRadius: '8px',
                                textTransform: 'none',
                                border: '1px solid rgba(0,0,0,0.1)',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  color: '#2f2f2f',
                                  borderColor: 'rgba(0,0,0,0.15)',
                                  boxShadow: '0 3px 8px rgba(0,0,0,0.15)'
                                },
                                '& .MuiButton-startIcon': {
                                  color: '#666',
                                  marginRight: { xs: '2px', sm: '4px' },
                                  '& svg': {
                                    filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.15))'
                                  }
                                },
                                textShadow: '0 1px 1px rgba(255,255,255,0.4)',
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' }
                              }}
                            >
                              Automate Contribution
                            </Button>
                          </motion.div>
                        )}
                    </Box>
                  </Box>

                  {/* Metrics Grid */}
                  <Grid container spacing={{ xs: 0.8, sm: 1 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                            marginBottom: { xs: 0.2, md: 0.3 },
                            lineHeight: 1.2
                          }}
                        >
                          {unitClass.totalValue === 0
                            ? '0'
                            : formatCurrency(unitClass.totalValue).replace('AUD', '').trim()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.9,
                            fontWeight: 500,
                            fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {isAdmin ? 'Total NAV' : 'Investment Value'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                            marginBottom: { xs: 0.2, md: 0.3 },
                            lineHeight: 1.2
                          }}
                        >
                          {unitClass.totalUnits.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.9,
                            fontWeight: 500,
                            fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {isAdmin ? 'Total Units' : 'My Units'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                            marginBottom: { xs: 0.2, md: 0.3 },
                            lineHeight: 1.2,
                            color:
                              unitClass.additionalMetricLabel === 'Monthly Income' &&
                              unitClass.additionalMetric < 0
                                ? '#ffcdd2'
                                : 'inherit'
                          }}
                        >
                          {unitClass.additionalMetric === 0
                            ? '0'
                            : formatCurrency(Math.abs(unitClass.additionalMetric))
                                .replace('AUD', '')
                                .trim()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.9,
                            fontWeight: 500,
                            fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                            lineHeight: 1.2
                          }}
                        >
                          {unitClass.additionalMetricLabel}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Content Section */}
                <CardContent sx={{ padding: { xs: 1.2, sm: 1.5, md: 2 } }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      marginBottom: { xs: 1, md: 1.5 },
                      lineHeight: 1.4,
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' }
                    }}
                  >
                    {unitClass.description}
                  </Typography>

                  {/* Enhanced Admin Details Table */}
                  {isAdmin && (
                    <TableContainer
                      component={Paper}
                      sx={{
                        mt: { xs: 1, md: 1.5 },
                        borderRadius: { xs: 1, md: 1.5 },
                        overflowX: 'auto'
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.6, sm: 0.8 }
                              }}
                            >
                              Admin Metric
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.6, sm: 0.8 }
                              }}
                            >
                              Value
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Amount Invested Till Date
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.amountInvestedTillDate !== undefined
                                ? formatCurrency(unitClass.amountInvestedTillDate)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Monthly Income
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 },
                                color:
                                  unitClass.totalMonthlyIncome !== undefined &&
                                  unitClass.totalMonthlyIncome < 0
                                    ? '#f44336'
                                    : 'inherit'
                              }}
                            >
                              {unitClass.totalMonthlyIncome !== undefined
                                ? formatCurrency(unitClass.totalMonthlyIncome)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Monthly Expense
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.totalMonthlyExpense !== undefined
                                ? formatCurrency(unitClass.totalMonthlyExpense)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Properties Value
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.totalPropertiesCurrentValue !== undefined
                                ? formatCurrency(unitClass.totalPropertiesCurrentValue)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Cash Used
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.totalCashUsed !== undefined
                                ? formatCurrency(unitClass.totalCashUsed)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Cash Remaining
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.cashRemaining !== undefined
                                ? formatCurrency(Number(unitClass.cashRemaining))
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Operation Charges
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.operationCharge !== undefined
                                ? formatCurrency(unitClass.operationCharge)
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Total Investors
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.investorCount || 0}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Created By
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {unitClass.createdBy || 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              Last Updated
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                fontWeight: 500,
                                py: { xs: 0.5, sm: 0.6 }
                              }}
                            >
                              {formatDateTime(unitClass.lastUpdatedDateTime)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Unified Unit Price Display */}
                  {(isAdmin || !isAdmin) && (
                    <>
                      <Divider sx={{ marginY: { xs: 1, md: 1.2 } }} />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: { xs: 1, md: 1.5 }
                        }}
                      >
                        {/* Unit Price */}
                        <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#999',
                              fontSize: { xs: '0.55rem', sm: '0.6rem' }
                            }}
                          >
                            Current Unit Price
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              color: '#2e7d32'
                            }}
                          >
                            ${unitClass.unitPrice.toFixed(4)}
                          </Typography>
                        </Box>

                        {/* Last Updated */}
                        <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#999',
                              fontSize: { xs: '0.55rem', sm: '0.6rem' }
                            }}
                          >
                            Last Updated
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.65rem', sm: '0.7rem' }
                            }}
                          >
                            {formatDateTime(unitClass.lastUpdatedDateTime)}
                          </Typography>
                        </Box>

                        {/* Admin: Total Investors */}
                        {isAdmin && (
                          <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#999',
                                fontSize: { xs: '0.55rem', sm: '0.6rem' }
                              }}
                            >
                              Total Investors
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' }
                              }}
                            >
                              {unitClass.investorCount || 0}
                            </Typography>
                          </Box>
                        )}

                        {/* Investor: My Units */}
                        {!isAdmin && unitClass.investorName && (
                          <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#999',
                                fontSize: { xs: '0.55rem', sm: '0.6rem' }
                              }}
                            >
                              My Units
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' }
                              }}
                            >
                              {unitClass.totalUnits.toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Navigation Shortcuts */}
      <ShortcutNavigation
        shortcuts={isAdmin ? adminNavigationShortcuts : investorNavigationShortcuts}
        title={isAdmin ? 'Fund Management Tools' : 'Investor Tools'}
        isAdmin={isAdmin}
      />

      {/* Automation Modal */}
      <AutomateContributionModal
        open={automationModalOpen}
        onClose={() => setAutomationModalOpen(false)}
        unitType={selectedUnitType}
      />
    </Container>
  );
};

export default FundManagementInvestment;
